// One number of the account, as a row of « Vos numéros » (/voice-numbers):
// its state, what it costs and until when, what came in recently, the agent
// answering it (a select), and in « ⋯ » the one way to stop it — cancel a
// billed number at the end of its period, remove one that is not billed —
// each behind a confirmation. A cancelled number can be kept until the end
// of its period (« Garder le numéro »).
// API: PUT /api/voice-numbers/{number}/agent, POST …/cancel, POST …/resume,
// DELETE /api/voice-numbers/{number} — each answers the whole page view.

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight, Phone, PhoneOff, Undo2, XCircle } from 'lucide-react';
import { Badge, ConfirmDialog, IconChip, OverflowMenu } from '../../design/ui';
import { cancelNumber, fail, formatDay, formatPrice, removeNumber, resumeNumber, setAgent } from './api';
import { isCanceling } from './voiceModel';
import { numberLabel } from './phoneFormat';

export const numberStatus = (item, t) => {
    const billing = item.billing;
    if (billing?.past_due) return ['danger', t('voiceSetup.status.pastDue', 'Paiement en échec')];
    if (isCanceling(item)) return ['warning', t('voiceSetup.status.canceling', 'Résilié')];
    if (!item.agent) return ['warning', t('voiceSetup.status.noAgent', 'Aucun agent ne répond')];
    return ['success', t('voiceSetup.status.active', 'Actif')];
};

const NumberCard = ({ item, agents, manage, onChange, onError }) => {
    const { t, i18n } = useTranslation();
    const [busy, setBusy] = useState(false);
    const [confirming, setConfirming] = useState(null);
    const lang = i18n?.language || 'fr';
    const billing = item.billing;
    const canceling = isCanceling(item);
    const until = formatDay(billing?.current_period_end, lang);
    const [tone, label] = numberStatus(item, t);
    const selectId = `voice-number-agent-${item.phone_number.replace(/\W/g, '')}`;

    const act = async (call, fallback) => {
        setBusy(true);
        try {
            const res = await call();
            setConfirming(null);
            onChange(res.data);
        } catch (err) {
            setConfirming(null);
            onError(fail(err, fallback));
        } finally {
            setBusy(false);
        }
    };

    let source;
    if (item.source === 'subscription') {
        source = canceling
            ? t('voiceSetup.source.canceling', 'Actif jusqu’au {{date}}, puis libéré.', { date: until })
            : t('voiceSetup.source.subscription', 'Abonnement {{price}} / mois · prochaine échéance le {{date}}', {
                price: formatPrice(billing?.monthly_price_eur, lang), date: until,
            });
    } else if (item.source === 'assigned') {
        source = t('voiceSetup.source.assigned', 'Attribué par Unanimity, sans abonnement.');
    } else {
        source = t('voiceSetup.source.external', 'Ajouté manuellement : Unanimity ne peut pas vérifier que les appels vers ce numéro arrivent jusqu’à l’agent.');
    }

    let activity;
    if (item.calls_30d > 0) {
        activity = t('voiceUi.numbers.activityCalls', item.calls_30d === 1 ? '{{count}} appel sur 30 jours · dernier le {{date}}' : '{{count}} appels sur 30 jours · dernier le {{date}}', {
            count: item.calls_30d, date: formatDay(item.last_call_at, lang),
        });
    } else if (item.last_call_at) {
        activity = t('voiceSetup.activity.quiet', 'Aucun appel sur 30 jours · dernier le {{date}}', { date: formatDay(item.last_call_at, lang) });
    } else {
        activity = t('voiceSetup.activity.none', 'Aucun appel pour l’instant. Appelez ce numéro pour essayer votre agent.');
    }

    const menu = manage ? [
        item.source === 'subscription' && !canceling && {
            label: t('voiceSetup.cancelSubscription', 'Résilier'),
            icon: XCircle,
            danger: true,
            onClick: () => setConfirming('cancel'),
        },
        item.source !== 'subscription' && {
            label: t('voiceSetup.remove', 'Retirer'),
            icon: PhoneOff,
            danger: true,
            onClick: () => setConfirming('remove'),
        },
    ].filter(Boolean) : [];

    return (
        <li className="u-row voice-v2__number" id={`voice-number-${item.phone_number.replace(/\W/g, '')}`}>
            <IconChip icon={Phone} tone={tone === 'success' ? 'success' : tone} />
            <div className="u-row__main">
                <div className="u-row__title">
                    <h3 className="voice-v2__number-title u-num">{numberLabel(item)}</h3>
                    <Badge tone={tone} dot>{label}</Badge>
                </div>
                <div className="u-row__meta u-row__meta--wrap">{source}</div>
                <div className="u-row__meta u-row__meta--wrap">{activity}</div>
                {item.agent_missing && (
                    <div className="u-row__meta u-row__meta--wrap voice-v2__gap">
                        {t('voiceSetup.agentGone', 'Ce numéro renvoyait vers un agent qui n’est pas un agent vocal de ce compte : choisissez-en un.')}
                    </div>
                )}
            </div>
            <div className="u-row__side voice-v2__number-side">
                <div className="voice-v2__number-agent">
                    <label className="u-overline" htmlFor={selectId}>{t('voiceSetup.answeredBy', 'Agent qui répond')}</label>
                    <select
                        id={selectId}
                        className="u-select"
                        value={item.agent?.id || ''}
                        disabled={!manage || busy}
                        onChange={(e) => act(
                            () => setAgent(item.phone_number, e.target.value),
                            t('voiceSetup.routeError', 'L’agent n’a pas pu être changé.'),
                        )}
                    >
                        <option value="">{t('voiceUi.numbers.noAgentOption', 'Aucun agent')}</option>
                        {agents.map((a) => <option key={a.id} value={a.id}>{a.name || t('voiceSetup.unnamedAgent', 'Agent sans nom')}</option>)}
                    </select>
                    {manage && agents.length === 0 && (
                        <Link className="u-link voice-v2__number-hint" to="/voice-agents">{t('voiceSetup.createAgentFirst', 'Créez d’abord un agent vocal')}</Link>
                    )}
                </div>
                {item.agent ? (
                    <Link
                        className="u-btn u-btn--ghost u-btn--icon u-btn--sm"
                        to={`/voice-agents/${item.agent.id}`}
                        aria-label={t('voiceSetup.openAgent', 'Ouvrir l’agent {{name}}', { name: item.agent.name })}
                        title={t('voiceSetup.openAgent', 'Ouvrir l’agent {{name}}', { name: item.agent.name })}
                    >
                        <ArrowUpRight aria-hidden="true" />
                    </Link>
                ) : <span className="voice-v2__icon-slot" aria-hidden="true" />}
                {manage && item.source === 'subscription' && canceling && (
                    <button
                        type="button"
                        className="u-btn u-btn--secondary u-btn--sm"
                        disabled={busy}
                        onClick={() => act(() => resumeNumber(item.phone_number), t('voiceSetup.resumeError', 'L’abonnement n’a pas pu être repris.'))}
                    >
                        <Undo2 aria-hidden="true" />
                        {t('voiceSetup.resume', 'Garder le numéro')}
                    </button>
                )}
                {menu.length > 0 && (
                    <OverflowMenu label={t('voiceUi.numbers.actions', 'Actions du {{number}}', { number: numberLabel(item) })} items={menu} />
                )}
            </div>

            {confirming === 'cancel' && (
                <ConfirmDialog
                    title={t('voiceUi.numbers.cancelTitle', 'Résilier le {{number}} ?', { number: numberLabel(item) })}
                    message={t('voiceUi.numbers.cancelText', 'Le numéro reste actif jusqu’au {{date}}, puis il est libéré : ses appels n’arriveront plus à l’agent. Vous pouvez le garder d’ici là.', { date: until })}
                    confirmLabel={t('voiceSetup.cancelDo', 'Confirmer la résiliation')}
                    cancelLabel={t('voiceSetup.keep', 'Garder le numéro')}
                    onConfirm={() => act(() => cancelNumber(item.phone_number), t('voiceSetup.cancelError', 'La résiliation a échoué.'))}
                    onCancel={() => setConfirming(null)}
                    busy={busy}
                    icon={XCircle}
                />
            )}
            {confirming === 'remove' && (
                <ConfirmDialog
                    title={t('voiceUi.numbers.removeTitle', 'Retirer le {{number}} ?', { number: numberLabel(item) })}
                    message={t('voiceUi.numbers.removeText', 'Le numéro ne répondra plus pour votre compte. Ses appels passés restent consultables.')}
                    confirmLabel={t('voiceSetup.removeDo', 'Retirer le numéro')}
                    cancelLabel={t('voiceSetup.cancel', 'Annuler')}
                    onConfirm={() => act(() => removeNumber(item.phone_number), t('voiceSetup.removeError', 'Le numéro n’a pas pu être retiré.'))}
                    onCancel={() => setConfirming(null)}
                    busy={busy}
                    icon={PhoneOff}
                />
            )}
        </li>
    );
};

export default NumberCard;
